import React, { useEffect, useState } from "react";
import Layout from "../../components/layout/layout";
import { fetchPublishedArticles } from "../../lib/articles";
import ArticleGrid from "../../components/articles/article-grid";
import HeroTemplateCandidats from "../../components/sections/HeroTemplateCandidats";
import { WebPageSchema, BreadcrumbSchema } from "../../lib/utils/schema-utils";

interface Article {
  id: string;
  title: string;
  content: string;
  excerpt?: string;
  image_url?: string;
  created_at: string;
  author?: string;
  category?: string;
  slug?: string;
}

export default function ArticlesPage() {
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(true);

  const categories = [
    { id: "all", name: "Toutes les catégories" },
    { id: "Recrutement", name: "Recrutement" },
    { id: "Marque employeur", name: "Marque employeur" },
    { id: "RH & Management", name: "RH & Management" },
    { id: "Onboarding", name: "Onboarding" },
    { id: "Culture d'entreprise", name: "Culture d'entreprise" },
    { id: "Innovation RH", name: "Innovation RH" },
    { id: "Général", name: "Général" },
  ];

  useEffect(() => {
    const loadArticles = async () => {
      setIsLoading(true);
      const data = await fetchPublishedArticles();
      setArticles(data);
      setIsLoading(false);
    };

    loadArticles();
  }, []);

  const filteredArticles =
    selectedCategory === "all"
      ? articles
      : articles.filter((article) => article.category === selectedCategory);

  return (
    <>
      <WebPageSchema
        page={{
          title: "Articles RH, Recrutement & Management",
          description:
            "Des contenus concrets et inspirants. Recrutement, onboarding, marque employeur et plus. Méthodes, outils, pratiques : on partage ce qui marque sur le terrain pour booster vos pratiques RH.",
          url: "https://recruteurs.io/ressources/articles",
          lastReviewed: "2025-05-22",
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: "Ressources", url: "/ressources" },
          { name: "Articles", url: "/ressources/articles" },
        ]}
      />

      <Layout>
        <>
          <HeroTemplateCandidats
            page="articles"
            section="hero"
            title={
              <>
                <span className="text-[42px] text-[initial] align-middle mr-2">📚</span>
                <span className="text-black">Articles RH, Recrutement</span>
                <br />
                <span className="text-gradient">& Management</span>
              </>
            }
            description={
              <p className="text-neutral-800">
                Des contenus concrets et inspirants. Recrutement, onboarding, marque employeur et plus encore.
                <br />
                Méthodes, outils, bonnes pratiques : on partage ce qui marque sur le terrain pour booster vos pratiques RH, recruter mieux, intégrer plus vite, et engager durablement vos équipes.
              </p>
            }
            imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/articles-recrutement-innovant.webp"
            cta1={{
              label: "Toutes les catégories",
              onClick: () => setSelectedCategory("all"),
            }}
            cta2={{
              label: "S'abonner à la newsletter",
              href: "#newsletter",
            }}
            breadcrumbs={[
              { name: "Ressources", href: "/ressources" },
              { name: "Articles", href: "/ressources/articles" },
            ]}
            customRight={
              <div
                className="hs-form-frame w-full"
                data-region="na1"
                data-form-id="9751b2d3-cee0-494f-b191-287b8bf3e2ab"
                data-portal-id="4621272"
              ></div>
            }
          />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Filtre de catégories */}
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold">Nos derniers articles</h2>
              <div className="flex gap-4 overflow-x-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    className={`text-sm px-4 py-2 rounded-full border ${
                      selectedCategory === cat.id
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-neutral-700 border-neutral-200"
                    }`}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Grille d'articles */}
            {isLoading ? (
              <p>Chargement des articles...</p>
            ) : (
              <ArticleGrid articles={filteredArticles} />
            )}

            {/* Cartes Pimp My Job & Mapping RH */}
            <div className="grid lg:grid-cols-2 gap-8 mt-12">
              {/* Carte Pimp My Job */}
              <div className="rounded-xl p-6 bg-gradient-to-br from-pink-100 via-yellow-100 to-blue-100 shadow-lg">
                <h3 className="text-2xl font-bold mb-2">Pimp My Job 🚀</h3>
                <p className="text-neutral-700 mb-4">
                  Générez des offres d'emploi irrésistibles grâce à l'IA. Augmentez le taux de candidature et démarquez-vous de la concurrence dès maintenant.
                </p>
                <a
                  href="https://pimpmyjob.recruteurs.io"
                  className="inline-block bg-black text-white px-5 py-3 rounded-xl hover:bg-neutral-800 transition"
                >
                  Tester gratuitement
                </a>
              </div>

              {/* Carte Mapping RH */}
              <div className="rounded-xl p-6 bg-gradient-to-r from-blue-100 to-purple-100 shadow-lg">
                <h3 className="text-2xl font-bold mb-2">Mapping RH 🧠</h3>
                <p className="text-neutral-700 mb-4">
                  Évaluez la compatibilité entre vos candidats et vos équipes. Optimisez vos prises de décision RH et réduisez le turnover dès aujourd'hui.
                </p>
                <a
                  href="/mapping-rh"
                  className="inline-block bg-black text-white px-5 py-3 rounded-xl hover:bg-neutral-800 transition"
                >
                  Tester le mapping
                </a>
              </div>
            </div>

            {/* Bloc SEO bas de page */}
            <div className="mt-16 prose prose-neutral max-w-none text-neutral-800">
              <h2>Cabinet de recrutement innovant à Marseille</h2>
              <p>
                Recruteurs.io est votre <strong>cabinet de recrutement innovant à Marseille</strong>, spécialisé dans l'accompagnement des entreprises B2B
                qui recherchent les meilleurs talents pour renforcer leurs équipes. Notre approche combine expertise RH, intelligence artificielle
                et méthodes de sourcing avancées pour vous garantir un recrutement rapide et performant.
              </p>
              <p>
                Grâce à notre implantation locale au cœur de la région Provence-Alpes-Côte d'Azur, nous connaissons parfaitement le marché
                marseillais et ses spécificités. Que vous soyez une PME ou un grand groupe industriel, nous adaptons notre stratégie pour répondre
                précisément à vos enjeux métier et culturels.
              </p>
              <p>
                Notre équipe RH à Marseille se concentre sur :
              </p>
              <ul>
                <li>
                  L'identification de candidats rares et à fort potentiel grâce à des outils de sourcing IA.
                </li>
                <li>
                  L'évaluation approfondie des compétences techniques et des soft skills pour un alignement optimal avec vos valeurs d'entreprise.
                </li>
                <li>
                  L'optimisation du processus de recrutement pour réduire le time-to-hire et maîtriser vos coûts.
                </li>
              </ul>
              <p>
                En choisissant Recruteurs.io, vous faites le choix d'un cabinet de recrutement innovant et engagé, dont la mission est de vous faire
                gagner du temps et de crédibiliser votre marque employeur à Marseille. Parlons de vos besoins RH et bâtissons ensemble votre stratégie
                de recrutement 100 % sur mesure.
              </p>
              <p>
                <em>Contactez-nous dès aujourd'hui pour une première consultation gratuite !</em>
              </p>
            </div>
          </div>
        </>
      </Layout>
    </>
  );
}